import { defineStore } from 'pinia'
import { reactive } from 'vue';
import { ref, computed } from 'vue'
import { MenuItem, WebConfig } from './types';

export const useGeneralStore = defineStore('general', () => {
    const theme = ref<string>(localStorage.getItem('theme') || 'light')
    const isDarkTheme = computed(() => theme.value === 'dark');
    const webConfig = reactive<WebConfig>({
        bio: "",
        homePage: null,
        contactPerson: null,
        contactEmail: null,
        contactPhone: null,
        contactAddress: null,
        hasMembership: false,
        memberCanRegister: false,
        memberCanLogin: false,
        socialMenu: [],
        footerMenu: [],
        headerMenu: [],
        supportMenu: [],
        companyMenu: [],
        ecosystemMenu: [],
    });

    const activateTheme = () => {
        // Remove both possible classes to ensure a clean state
        document.documentElement.classList.remove('light', 'dark')
        document.documentElement.classList.add(theme.value)
    }

    const setTheme = (themeValue: string) => {
        if (theme.value) {
            document.documentElement.classList.remove(theme.value as string);
        }

        theme.value = themeValue;
        localStorage.setItem('theme', themeValue);
        activateTheme();
    }

    const loadWebConfig = async () => {
        try {
            var query = `query GetWebConfig {
                webConfiguration {
                    bio
                    homePage
                    contactPerson
                    contactEmail
                    contactPhone
                    contactAddress
                    hasMembership
                    memberCanRegister
                    memberCanLogin
                }
                menus(where:{status:{equalTo:"active"}}) {
                    id
                    name
                    link
                    type
                    position
                    icon
                    linkPosition
                    status
                }
            }`;

            var res = await window.$ajaxGraphql(query, {}, "/graphql");

            if (res) {
                if (res.webConfiguration) {
                    webConfig.bio = res.webConfiguration.bio;
                    webConfig.homePage = res.webConfiguration.homePage;
                    webConfig.contactPerson = res.webConfiguration.contactPerson;
                    webConfig.contactEmail = res.webConfiguration.contactEmail;
                    webConfig.contactPhone = res.webConfiguration.contactPhone;
                    webConfig.contactAddress = res.webConfiguration.contactAddress;
                    webConfig.hasMembership = res.webConfiguration.hasMembership;
                    webConfig.memberCanRegister = res.webConfiguration.memberCanRegister;
                    webConfig.memberCanLogin = res.webConfiguration.memberCanLogin;
                }

                if (res.menus && res.menus.length > 0) {
                    res.menus.forEach((menu: MenuItem) => {
                        if (menu.linkPosition === "social") {
                            webConfig.socialMenu.push(menu);
                        } else if (menu.linkPosition === "footer") {
                            webConfig.footerMenu.push(menu);
                        } else if (menu.linkPosition === "header") {
                            webConfig.headerMenu.push(menu);
                        } else if (menu.linkPosition === "support") {
                            webConfig.supportMenu.push(menu);
                        } else if (menu.linkPosition === "company") {
                            webConfig.companyMenu.push(menu);
                        } else if (menu.linkPosition === "ecosystem") {
                            webConfig.ecosystemMenu.push(menu);
                        }
                    });

                    webConfig.headerMenu.sort((a, b) => a.position - b.position);
                    webConfig.supportMenu.sort((a, b) => a.position - b.position);
                    webConfig.companyMenu.sort((a, b) => a.position - b.position);
                    webConfig.footerMenu.sort((a, b) => a.position - b.position);
                    webConfig.socialMenu.sort((a, b) => a.position - b.position);
                    webConfig.ecosystemMenu.sort((a, b) => a.position - b.position);
                }
            }
        } catch (error) {
            console.log('Error loading web config:', error);
        }
    }

    const restoreSession = () => {
        try {
            const savedTheme = localStorage.getItem('theme')
            if (savedTheme) {
                theme.value = savedTheme
            }

            activateTheme();
        } catch (error) {
            console.log('Error restoring general store session:', error);
        }

        loadWebConfig();
    }

    const toggleTheme = () => {
        if (theme.value === 'dark') {
            setTheme('light')
        } else {
            setTheme('dark')
        }
    }

    return {
        theme,
        isDarkTheme,
        webConfig,
        setTheme,
        toggleTheme,
        restoreSession,
        loadWebConfig,
    }
})
